/* eslint-disable max-lines-per-function */

import { useToggle } from 'react-use';

import { VisitSettlement } from '@dreryk/egabinet-financial-settlement-frontend-api';
import { TaxIdentifier } from '@dreryk/tax-identifier-common-api';

import { commonTranslationKeys, TranslatableText } from '@dreryk/react-common';
import {
   ButtonVariant,
   Modal,
   ModalCancelButton,
   ModalCloseButton,
   ModalConfirmButton,
   ModalWidth,
} from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import { VisitSettlementForm, VisitSettlementFormInitializer } from '../shared/form/VisitSettlementForm';
import { useRefetchVisitSettlement } from '../shared/useRefetchVisitSettlement';

import {
   VisitSettlementPaymentCreateReceiptAction,
   VisitSettlementUpdateMedicalServicesAction,
   VisitSettlementUpdatePaymentAction,
} from './VisitSettlementPaymentModal.actions';
import { getLabelForVisitSettlementPaymentActions } from './VisitSettlementPaymentModal.utils';

type VisitSettlementPaymentModalOnSuccessOptions = {
   withInvoiceCreation: boolean;
   visitSettlement?: VisitSettlement | null;
   taxIdentifierForReceipt: TaxIdentifier | undefined;
};

type VisitSettlementPaymentModalProps = {
   readonly appointmentId: number;
   readonly visitDate: Date;
   readonly practitionerId: number;
   readonly onCancel: VoidFunction;
   readonly onSuccess: (options: VisitSettlementPaymentModalOnSuccessOptions) => void;
   readonly onPayment: VoidFunction;
   readonly initializer: VisitSettlementFormInitializer;
   readonly ownedByCurrentUser: boolean;
   readonly fiscalPrintEnabled: boolean;
   readonly invoiceProviderIntegrationEnabled?: boolean;
   readonly title: TranslatableText;
};

export const VisitSettlementPaymentModal = (props: VisitSettlementPaymentModalProps): JSX.Element => {
   const [settled, setSettled] = useToggle(false);
   const refetchVisitSettlement = useRefetchVisitSettlement();

   return (
      <Modal testId={'visitSettlementPayment'} title={props.title} width={ModalWidth.NORMAL}>
         <VisitSettlementForm
            appointmentId={props.appointmentId}
            practitionerId={props.practitionerId}
            initializer={props.initializer}
            fiscalPrintEnabled={props.fiscalPrintEnabled}
         >
            {({ handleSubmit, formState, payment }) => {
               const label = getLabelForVisitSettlementPaymentActions(props.ownedByCurrentUser, payment.enabled);

               return (
                  <>
                     <VisitSettlementUpdateMedicalServicesAction
                        appointmentId={props.appointmentId}
                        silent={payment.enabled}
                        onVisitAlreadySettledFault={setSettled}
                     >
                        {({ updateVisitSettlement }) => (
                           <VisitSettlementUpdatePaymentAction
                              appointmentId={props.appointmentId}
                              onVisitAlreadySettledFault={setSettled}
                           >
                              {({ updatePayment }) => (
                                 <VisitSettlementPaymentCreateReceiptAction
                                    appointmentId={props.appointmentId}
                                    visitDate={props.visitDate}
                                    paymentMethod={payment.method}
                                    paymentMethodDescription={payment.methodDescription}
                                 >
                                    {({ createReceipt }) => {
                                       const handleUpdateSettlementOrPayment = async (options: {
                                          withInvoiceCreation: boolean;
                                       }): Promise<void> => {
                                          await handleSubmit(
                                             async ({ visitSettlement, payment, taxIdentifierForReceipt }) => {
                                                if (visitSettlement.editable) {
                                                   await updateVisitSettlement(visitSettlement);
                                                }

                                                await refetchVisitSettlement();

                                                if (payment.enabled) {
                                                   await updatePayment(payment);
                                                   props.onPayment();
                                                }

                                                await refetchVisitSettlement();

                                                if (
                                                   props.fiscalPrintEnabled &&
                                                   payment.enabled &&
                                                   !options.withInvoiceCreation
                                                ) {
                                                   const currentVisitSettlement = await createReceipt(
                                                      taxIdentifierForReceipt
                                                   );

                                                   return props.onSuccess({
                                                      ...options,
                                                      visitSettlement: currentVisitSettlement,
                                                      taxIdentifierForReceipt: taxIdentifierForReceipt,
                                                   });
                                                }

                                                return props.onSuccess({
                                                   ...options,
                                                   taxIdentifierForReceipt: taxIdentifierForReceipt,
                                                });
                                             }
                                          )();
                                       };

                                       return (
                                          <>
                                             {protectFeature(
                                                'creatingInvoices',
                                                payment.enabled && props.invoiceProviderIntegrationEnabled ? (
                                                   <ModalConfirmButton
                                                      label={label.invoice!}
                                                      disabled={settled}
                                                      variant={ButtonVariant.SECONDARY}
                                                      testId={'settleAndCreateInvoice'}
                                                      onClick={async () => {
                                                         await handleUpdateSettlementOrPayment({
                                                            withInvoiceCreation: true,
                                                         });
                                                      }}
                                                   />
                                                ) : null,
                                                null
                                             )}

                                             <ModalConfirmButton
                                                label={label.settlement}
                                                onClick={async () => {
                                                   await handleUpdateSettlementOrPayment({
                                                      withInvoiceCreation: false,
                                                   });
                                                }}
                                                disabled={settled}
                                                variant={ButtonVariant.PRIMARY}
                                             />
                                          </>
                                       );
                                    }}
                                 </VisitSettlementPaymentCreateReceiptAction>
                              )}
                           </VisitSettlementUpdatePaymentAction>
                        )}
                     </VisitSettlementUpdateMedicalServicesAction>

                     <ModalCancelButton
                        label={commonTranslationKeys.cancel}
                        onClick={props.onCancel}
                        shouldConfirmExit={!settled && formState.isDirty}
                     />
                     <ModalCloseButton onClick={props.onCancel} shouldConfirmExit={!settled && formState.isDirty} />
                  </>
               );
            }}
         </VisitSettlementForm>
      </Modal>
   );
};
